import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Input, Button, Typography, Avatar } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import { userListForClient } from '../../users/users.service'
import { loginState } from '../../login/login.slice'
import styles from './AssigneeSelector.module.css'

const { Text } = Typography

const AssigneeSelector = ({
    onSelect,
    selectedAssignee,
    projectId,
    onClose,
    taskListKind = 'support',
    open = false,
    onTabNext,
}) => {
    const { userProfile } = useSelector(loginState)
    const [searchQuery, setSearchQuery] = useState('')
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const searchInputRef = useRef(null)
    const listRef = useRef(null)
    const usersRef = useRef([])
    const loadingMoreRef = useRef(false)
    const skipInitialSearchDebounceRef = useRef(true)
    const fetchUsersRef = useRef(null)
    const limit = 10

    const getInitials = (name) => {
        const text = name == null ? '' : String(name).trim()
        if (!text) return 'U'
        const parts = text.split(/\s+/).filter(Boolean)
        if (parts.length >= 2) {
            const a = parts[0][0]
            const b = parts[1][0]
            if (a != null && b != null) {
                return `${a}${b}`.toUpperCase()
            }
        }
        return text.slice(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const fetchUsers = useCallback(
        async (search = '', skipValue = 0, append = false) => {
            setLoading(true)
            try {
                const response = await userListForClient({
                    skip: skipValue,
                    limit: limit,
                    search: search,
                    filter: {},
                    projectId: projectId,
                    ...(taskListKind === 'main' ? { type: 'pms' } : {}),
                })

                if (response?.data) {
                    const newUsers = response.data || []
                    if (append) {
                        setUsers((prev) => {
                            const seen = new Set(
                                prev.map((u) => u._id).filter(Boolean)
                            )
                            const merged = [...prev]
                            for (const u of newUsers) {
                                const id = u?._id
                                if (id && !seen.has(id)) {
                                    seen.add(id)
                                    merged.push(u)
                                }
                            }
                            return merged
                        })
                    } else {
                        setUsers(newUsers)
                    }
                    setHasMore(response.isNext || false)
                } else {
                    if (!append) {
                        setUsers([])
                    }
                    setHasMore(false)
                }
            } catch (error) {
                console.error('Failed to fetch users', error)
                if (!append) {
                    setUsers([])
                }
            } finally {
                setLoading(false)
            }
        },
        [projectId, taskListKind]
    )

    fetchUsersRef.current = fetchUsers

    const debouncedSearchRef = useRef(
        debounce((query) => {
            fetchUsersRef.current?.(query, 0, false)
        }, 500)
    )

    useEffect(() => {
        setSearchQuery('')
        fetchUsers('', 0, false)
    }, [fetchUsers])

    useEffect(() => {
        if (skipInitialSearchDebounceRef.current) {
            skipInitialSearchDebounceRef.current = false
            return
        }
        debouncedSearchRef.current(searchQuery)
    }, [searchQuery])

    useEffect(() => {
        usersRef.current = users
    }, [users])

    useEffect(() => {
        const d = debouncedSearchRef.current
        return () => d.cancel()
    }, [])

    useEffect(() => {
        if (!open) return
        const frameId = requestAnimationFrame(() => {
            searchInputRef.current?.focus?.()
        })
        return () => cancelAnimationFrame(frameId)
    }, [open])

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value)
    }

    const handleAssignToMe = (e) => {
        e?.stopPropagation?.()
        e?.preventDefault?.()

        const assigneeId =
            userProfile?._id || userProfile?.id || userProfile?.userId
        if (!assigneeId) return

        onSelect({
            _id: assigneeId,
            name:
                userProfile?.name ||
                userProfile?.fullName ||
                userProfile?.username ||
                userProfile?.email ||
                'Me',
            email: userProfile?.email,
            profilePicture: userProfile?.profilePicture,
        })
    }

    const handleUserSelect = (user) => {
        onSelect({
            _id: user._id,
            name: user.name,
            email: user.email,
            profilePicture: user.profilePicture,
        })
    }

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target
        if (
            scrollHeight - scrollTop > clientHeight + 10 ||
            !hasMore ||
            loading ||
            loadingMoreRef.current
        ) {
            return
        }
        loadingMoreRef.current = true
        const nextSkip = usersRef.current.length
        fetchUsers(searchQuery, nextSkip, true).finally(() => {
            loadingMoreRef.current = false
        })
    }

    return (
        <div
            className={styles.assigneeSelectorPopup}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div className={styles.assigneeSelectorHeader}>
                <Text className={styles.assigneeSelectorTitle}>Assignee</Text>
                <Icon
                    icon="mdi:close"
                    width={20}
                    height={20}
                    onClick={onClose}
                    style={{
                        cursor: 'pointer',
                        color: '#64748b',
                    }}
                />
            </div>

            {/* Search and Assign to Me */}
            <div className={styles.assigneeSelectorSearch}>
                <Input
                    ref={searchInputRef}
                    placeholder="Name or email"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                        if (e.key === 'Tab' && !e.shiftKey && onTabNext) {
                            e.preventDefault()
                            onTabNext()
                        }
                    }}
                    className={styles.assigneeSearchInput}
                    prefix={
                        <Icon
                            icon="mdi:magnify"
                            width={16}
                            height={16}
                            style={{ color: '#94a3b8' }}
                        />
                    }
                />
                <div className={styles.assigneeSelectorOr}>or</div>
                <Button
                    type="text"
                    onClick={handleAssignToMe}
                    className={styles.assignToMeButton}
                >
                    Assign to Me
                </Button>
            </div>

            {/* Users List */}
            <div
                ref={listRef}
                className={styles.assigneeSelectorList}
                onScroll={handleScroll}
                style={{
                    scrollbarColor: '#a0a1a1 #fffbfb',
                    scrollbarWidth: 'thin',
                }}
            >
                {loading && users?.length === 0 ? (
                    <div className={styles.assigneeSelectorLoading}>
                        <Icon
                            icon="line-md:loading-loop"
                            width={24}
                            height={24}
                            style={{ color: '#6366f1' }}
                        />
                    </div>
                ) : users?.length === 0 ? (
                    <div className={styles.assigneeSelectorEmpty}>
                        <Text style={{ color: '#94a3b8' }}>No users found</Text>
                    </div>
                ) : (
                    users?.map((user) => (
                        <div
                            key={user._id}
                            className={styles.assigneeSelectorItem}
                            onClick={() => handleUserSelect(user)}
                        >
                            <Avatar
                                size={26}
                                src={user.profilePicture}
                                style={{
                                    backgroundColor: getAssigneeColor(
                                        user.name
                                    ),
                                    flexShrink: 0,
                                    fontSize: 14,
                                }}
                            >
                                {getInitials(user.name)}
                            </Avatar>
                            <div className={styles.assigneeSelectorItemInfo}>
                                <Text className={styles.assigneeSelectorName}>
                                    {user.name}
                                </Text>
                                <Text className={styles.assigneeSelectorEmail}>
                                    {user.email}
                                </Text>
                            </div>
                        </div>
                    ))
                )}
                {loading && users?.length > 0 && (
                    <div className={styles.assigneeSelectorLoadingMore}>
                        <Icon
                            icon="line-md:loading-loop"
                            width={20}
                            height={20}
                            style={{ color: '#6366f1' }}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default AssigneeSelector
